<template>
    <div class="flex w-full flex-col  at600:flex-row gap-4 items-center justify-between mb-6">
        <h1 class="sr-only">Resources</h1>
        <h3 class="font-semibold whitespace-nowrap text-xl">
            <template v-if="!userSelectionsMade">{{ $t('Recent Videos') }}</template>
            <template v-else>
                <div v-if="userSelectionsMade && !searchIsLoading && searchHasResults">
                    {{ searchTotalResults }} results for "{{ searchTerm}}"
                </div>
            </template>
        </h3>
        <div 
            class="w-full max-w-[600px] flex justify-end flex-col-reverse at600:flex-row items-center gap-4"
            v-if="!settings.is_sourcing_mode"
        >
            <div class="w-full at600:w-auto flex justify-end gap-4  items-center">
                <SearchInput v-model="searchTerm" :medium="true" />
            </div>
        </div>
    </div>
    <div v-if="userSelectionsMade && searchHasResults && !searchIsLoading" ref="results">
        <ul class="border-t border-keyline-primary">
            <li v-for="resource in searchResults">
                <ResourceCard :resource="resource" :elasticsearch="true" />
            </li>
        </ul>
        <div class="mt-8">
            <SearchPagination
                v-if="searchHasResults && searchTotalResults > searchResultsPerPage"
                :total-records="Math.min(searchTotalResults, 10000)"
                :per-page="searchResultsPerPage"
                :current-page="searchCurrentPage"
                @update:currentPage="
                    (page) => {
                        searchCurrentPage = page;
                        scrollToTop();
                    }
                "
            />
        </div>
    </div>
    <div v-if="userSelectionsMade && !searchHasResults && !searchIsLoading">
        <RecordsNotFound message="There are no resources that match your query." />
    </div>
    <div v-if="!userSelectionsMade" class="grid gap-8 grid-cols-1 at1024:grid-cols-3">
        <ResourceCard :resource="resource" v-for="resource in newVideos" :card-format="true" />
    </div>
    <div v-if="searchIsLoading">
        <LoadingSpinner :large="true" />
    </div>
</template>

<script setup>
import {useAppSearchGeneric, useSettingsLoader, useUser} from "@/Composables";
import {computed, ref, watch} from "vue";
import {
    ResourceCard,
    SearchInput,
    RecordsNotFound,
    LoadingSpinner,
    SearchPagination,
} from "@/Components";

const props = defineProps({
    manufacturers: {
        type: Array,
        required: true,
    },
    newVideos: {
        type: Array,
        required: true,
    },
    // Allow external components to two-way bind a search term using v-model
    modelValue: {
        type: String,
        default: "",
    },
});

// Emit event for v-model updates
const emit = defineEmits(["update:modelValue"]);

const {
    searchTerm,
    searchIsLoading,
    searchResults,
    searchTotalResults,
    searchHasResults,
    searchFacets,
    searchWasSearched,
    searchClearFilters,
    searchState,
    searchReset,
    searchSuggestions,
    searchTrackClick,
    searchAppliedFilters,
    searchCurrentPage,
    searchSortBy,
    searchResultsPerPage,
    searchAddFilter,
    searchSetFilter,
    searchRemoveFilter,
} = useAppSearchGeneric(useSettingsLoader().settings('resourceSearch'));

const results = ref(null);

const { settings } = useUser();

const userSelectionsMade = computed(() => {
    return !!(searchState.value.filters?.length > 1 || searchTerm.value?.length);
});

const scrollToTop = () => results.value.scrollTop;

const selectedManufacturer = ref('');

// Synchronize external modelValue with internal searchTerm and vice-versa
watch(
    () => props.modelValue,
    (newVal) => {
        if (newVal !== undefined && newVal !== searchTerm.value) {
            searchTerm.value = newVal;
        }
    }
);

watch(
    searchTerm,
    (newVal) => {
        if (newVal !== props.modelValue) {
            emit('update:modelValue', newVal);
        }
    }
);


</script>
